/**
 * Reading a picture for placing on the page.
 *
 * A picture reaches a free layer two ways: chosen in the panel, or dropped
 * straight onto the page. Both have to refuse the same files for the same
 * reasons and both have to know the picture's proportions, so the frame it
 * lands in fits it instead of squashing it. Those two things are here, and
 * nothing else: where the picture goes is the rules' business
 * (`imageBoxAt`, `fittedToImage` in `domain/free.ts`).
 */

/**
 * The largest picture taken, as the image section takes.
 *
 * A picture is stored in the report as a data URL, so every megabyte is a
 * megabyte on every save and every export; past this, it is a photograph
 * straight off a camera that nobody meant to put in a report at full size.
 */
export const MOST_PICTURE_BYTES = 4 * 1024 * 1024

/** Why a file was not taken. */
export type PictureRefusal = 'notAnImage' | 'tooLarge' | 'unreadable'

export type PictureRead =
  | { readonly ok: true; readonly source: string }
  | { readonly ok: false; readonly why: PictureRefusal }

/** A picture file as a data URL, or why it cannot be one. */
export function readPicture(file: File): Promise<PictureRead> {
  if (!file.type.startsWith('image/')) return Promise.resolve({ ok: false, why: 'notAnImage' })
  if (file.size > MOST_PICTURE_BYTES) return Promise.resolve({ ok: false, why: 'tooLarge' })

  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(
        typeof reader.result === 'string'
          ? { ok: true, source: reader.result }
          : { ok: false, why: 'unreadable' },
      )
    }
    reader.onerror = () => resolve({ ok: false, why: 'unreadable' })
    reader.readAsDataURL(file)
  })
}

/**
 * A picture's width over its height, or NaN when it cannot be decoded.
 *
 * NaN rather than a guess, so the caller's rule decides what a picture of
 * unknown shape is taken to be, and it is decided in one place.
 */
export function aspectOf(source: string): Promise<number> {
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => {
      resolve(image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : Number.NaN)
    }
    image.onerror = () => resolve(Number.NaN)
    image.src = source
  })
}

/** The first picture among some dropped files, or nothing. */
export function firstPicture(files: FileList | null | undefined): File | null {
  if (!files) return null
  return [...files].find((file) => file.type.startsWith('image/')) ?? null
}
